import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin, Shield, Truck } from 'lucide-react';

const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear();

    const footerSections = [
        {
            title: 'Atención al Cliente',
            links: [
                { name: 'Centro de Ayuda', href: '/help' },
                { name: 'Cómo Comprar', href: '/how-to-buy' },
                { name: 'Devoluciones', href: '/returns' },
                { name: 'Garantías', href: '/warranties' },
                { name: 'Contactanos', href: '/contact' },
            ]
        },
        {
            title: 'Sobre Eisgemi',
            links: [
                { name: 'Quiénes Somos', href: '/about' },
                { name: 'Trabaja con Nosotros', href: '/careers' },
                { name: 'Prensa', href: '/press' },
                { name: 'Inversionistas', href: '/investors' },
                { name: 'Sustentabilidad', href: '/sustainability' },
            ]
        },
        {
            title: 'Vender',
            links: [
                { name: 'Vende en Eisgemi', href: '/sell' },
                { name: 'Portal de Vendedores', href: '/seller/register' },
                { name: 'Centro de Vendedores', href: '/seller/help' },
                { name: 'Universidad del Vendedor', href: '/seller/training' },
                { name: 'Publicidad', href: '/advertising' },
            ]
        },
        {
            title: 'Categorías',
            links: [
                { name: 'Electrónicos', href: '/category/electronics' },
                { name: 'Moda', href: '/category/fashion' },
                { name: 'Hogar y Jardín', href: '/category/home' },
                { name: 'Deportes', href: '/category/sports' },
                { name: 'Belleza', href: '/category/beauty' },
            ]
        }
    ];

    const paymentMethods = [
        { name: 'Visa', icon: '💳' },
        { name: 'Mastercard', icon: '💳' },
        { name: 'American Express', icon: '💳' },
        { name: 'PayPal', icon: '🏦' },
        { name: 'Yape', icon: '📱' },
        { name: 'Plin', icon: '📱' },
    ];

    const securityBadges = [
        { name: 'SSL Seguro', icon: <Shield className="w-5 h-5" /> },
        { name: 'Compra Protegida', icon: <Shield className="w-5 h-5" /> },
        { name: 'Envío Seguro', icon: <Truck className="w-5 h-5" /> },
    ];

    return (
        <footer className="bg-gray-900 text-white">
            {/* Newsletter Section */}
            <div className="bg-primary-600 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        <div className="mb-4 md:mb-0">
                            <h3 className="text-xl font-bold mb-2">¡Suscríbete a nuestro newsletter!</h3>
                            <p className="text-primary-100">Recibe ofertas exclusivas y novedades</p>
                        </div>

                        <div className="flex w-full md:w-auto max-w-md">
                            <input
                                type="email"
                                placeholder="Tu email aquí..."
                                className="flex-1 px-4 py-3 rounded-l-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
                            />
                            <button className="bg-primary-800 hover:bg-primary-900 px-6 py-3 rounded-r-lg font-medium transition-colors">
                                Suscribirme
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Footer Content */}
            <div className="py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                        {/* Company Info */}
                        <div className="lg:col-span-1">
                            <Link to="/" className="flex items-center space-x-2 mb-4">
                                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">T</span>
                                </div>
                                <span className="text-xl font-bold">Eisgemi</span>
                            </Link>

                            <p className="text-gray-400 mb-6 text-sm">
                                Tu marketplace de confianza con los mejores precios y productos de calidad.
                            </p>

                            {/* Contact Info */}
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center space-x-3">
                                    <MapPin className="w-4 h-4 text-primary-400" />
                                    <span className="text-gray-400">Lima, Perú</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <Phone className="w-4 h-4 text-primary-400" />
                                    <span className="text-gray-400">+51 1 234-5678</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <Mail className="w-4 h-4 text-primary-400" />
                                    <span className="text-gray-400">hola@Eisgemi.com</span>
                                </div>
                            </div>
                        </div>

                        {/* Footer Links */}
                        {footerSections.map((section, index) => (
                            <div key={index}>
                                <h4 className="text-white font-semibold mb-4">{section.title}</h4>
                                <ul className="space-y-3">
                                    {section.links.map((link, linkIndex) => (
                                        <li key={linkIndex}>
                                            <Link
                                                to={link.href}
                                                className="text-gray-400 hover:text-primary-400 transition-colors text-sm"
                                            >
                                                {link.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Payment Methods & Security */}
            <div className="border-t border-gray-800 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
                        {/* Payment Methods */}
                        <div>
                            <h5 className="text-sm font-semibold mb-3">Métodos de Pago</h5>
                            <div className="flex items-center space-x-4">
                                {paymentMethods.map((method, index) => (
                                    <div
                                        key={index}
                                        className="w-12 h-8 bg-white rounded flex items-center justify-center text-lg"
                                        title={method.name}
                                    >
                                        {method.icon}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Security Badges */}
                        <div>
                            <h5 className="text-sm font-semibold mb-3">Compra Segura</h5>
                            <div className="flex items-center space-x-4">
                                {securityBadges.map((badge, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center space-x-2 text-xs text-gray-400"
                                    >
                                        {badge.icon}
                                        <span>{badge.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Social Media */}
                        <div>
                            <h5 className="text-sm font-semibold mb-3">Síguenos</h5>
                            <div className="flex items-center space-x-4">
                                <a
                                    href="https://facebook.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-primary-400 transition-colors"
                                >
                                    <Facebook className="w-5 h-5" />
                                </a>
                                <a
                                    href="https://twitter.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-primary-400 transition-colors"
                                >
                                    <Twitter className="w-5 h-5" />
                                </a>
                                <a
                                    href="https://instagram.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-primary-400 transition-colors"
                                >
                                    <Instagram className="w-5 h-5" />
                                </a>
                                <a
                                    href="https://youtube.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-primary-400 transition-colors"
                                >
                                    <Youtube className="w-5 h-5" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-800 py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                        <p className="text-gray-400 text-sm">
                            © {currentYear} Eisgemi. Todos los derechos reservados.
                        </p>

                        <div className="flex items-center space-x-6 text-sm">
                            <Link to="/privacy" className="text-gray-400 hover:text-primary-400 transition-colors">
                                Política de Privacidad
                            </Link>
                            <Link to="/terms" className="text-gray-400 hover:text-primary-400 transition-colors">
                                Términos y Condiciones
                            </Link>
                            <Link to="/cookies" className="text-gray-400 hover:text-primary-400 transition-colors">
                                Política de Cookies
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;